import { useEffect, useState } from "react";
import { Database, HardDrive, KeyRound, Loader2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/ui/data-table";
import { useAuth } from "@/App";
import { backupsApi, type Backup, type BackupAccessToken } from "@/lib/backups.api";
import { SettingsKpi } from "../components/SettingsKpi";
import { getApiErrorMessage } from "../settingsPage.helpers";
import { createBackupColumns } from "./backupColumns";
import { formatBytes, formatDateTime, relativeTimeFromNow } from "./backups.helpers";
import { RestoreDrillPanel } from "./RestoreDrillPanel";

export function BackupsTab() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [backups, setBackups] = useState<Backup[]>([]);
  const [tokens, setTokens] = useState<BackupAccessToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [newTokenLabel, setNewTokenLabel] = useState("");
  const [creatingToken, setCreatingToken] = useState(false);
  const [justCreatedToken, setJustCreatedToken] = useState<string | null>(null);

  async function loadAll() {
    setLoading(true);
    try {
      // Token management stays super_admin-only (C2) — dev never calls it.
      const [backupsRes, tokensRes] = await Promise.all([
        backupsApi.list(),
        isSuperAdmin ? backupsApi.listTokens() : Promise.resolve({ data: [] as BackupAccessToken[] }),
      ]);
      setBackups(backupsRes.data);
      setTokens(tokensRes.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible de charger les sauvegardes."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user?.role === "dev" || isSuperAdmin) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  if (!user || (user.role !== "dev" && user.role !== "super_admin")) {
    return (
      <div className="border border-warning-border bg-warning-bg px-4 py-3 text-sm text-warning-text">
        Ces outils sont réservés au super administrateur et au compte dev/supervision.
      </div>
    );
  }

  async function handleRun() {
    setRunning(true);
    try {
      const response = await backupsApi.run();
      if (response.data.status === "failed") {
        toast.error(response.data.errorMessage ?? "La sauvegarde a échoué.");
      } else {
        toast.success("Sauvegarde effectuée avec succès.");
      }
      await loadAll();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible de lancer la sauvegarde."));
    } finally {
      setRunning(false);
    }
  }

  async function handleRetry(id: number) {
    setRetryingId(id);
    try {
      const response = await backupsApi.retry(id);
      if (response.data.status === "failed") {
        toast.error(response.data.errorMessage ?? "La nouvelle tentative a échoué.");
      } else {
        toast.success("Nouvelle tentative réussie.");
      }
      await loadAll();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible de relancer la sauvegarde."));
    } finally {
      setRetryingId(null);
    }
  }

  async function handleCreateToken() {
    const label = newTokenLabel.trim();
    if (!label) return;
    setCreatingToken(true);
    try {
      const response = await backupsApi.createToken(label);
      setJustCreatedToken(response.data.token);
      setNewTokenLabel("");
      await loadAll();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible de créer le jeton."));
    } finally {
      setCreatingToken(false);
    }
  }

  async function handleRevokeToken(id: number) {
    try {
      await backupsApi.revokeToken(id);
      toast.success("Jeton révoqué.");
      await loadAll();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible de révoquer le jeton."));
    }
  }

  const lastBackup = backups[0];
  const totalBytes = backups.reduce(
    (sum, b) => sum + (b.fileSizeBytes && !b.prunedAt ? Number(b.fileSizeBytes) : 0),
    0,
  );
  const activeTokens = tokens.filter((t) => !t.revokedAt).length;

  const columns = createBackupColumns({ onRetry: handleRetry, retryingId });

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-3">
        <SettingsKpi
          icon={Database}
          label="Dernière sauvegarde"
          value={lastBackup ? relativeTimeFromNow(lastBackup.startedAt) : "Aucune"}
          tone={lastBackup?.status === "failed" ? "danger" : "neutral"}
        />
        <SettingsKpi icon={HardDrive} label="Espace utilisé (VPS)" value={formatBytes(totalBytes)} />
        <SettingsKpi icon={KeyRound} label="Jetons d'accès local actifs" value={String(activeTokens)} />
      </div>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Historique des sauvegardes</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Rotation automatique quotidien / hebdomadaire / mensuel / annuel, chaque nuit à 02h00.
            </p>
          </div>
          <Button size="sm" onClick={handleRun} disabled={running}>
            {running ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />}
            Lancer une sauvegarde maintenant
          </Button>
        </div>

        <DataTable
          columns={columns}
          data={backups}
          loading={loading}
          emptyMessage="Aucune sauvegarde pour le moment."
          pageSize={8}
        />
      </section>

      <RestoreDrillPanel backups={backups} />

      {isSuperAdmin && (
      <section className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">Jetons d'accès local</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Utilisés par le script de synchronisation locale (copie automatique sur un poste de
            l'agence). Accès limité au téléchargement des sauvegardes, révocable à tout moment.
          </p>
        </div>

        {justCreatedToken && (
          <div className="mx-4 mt-3 border border-warning-border bg-warning-bg px-3 py-2.5 text-xs text-warning-text">
            <p className="font-medium">Jeton créé — copiez-le maintenant, il ne sera plus jamais affiché :</p>
            <code className="mt-1.5 block break-all rounded bg-card px-2 py-1.5 text-[11px]">
              {justCreatedToken}
            </code>
            <Button
              size="sm"
              variant="secondary"
              className="mt-2"
              onClick={() => setJustCreatedToken(null)}
            >
              J'ai copié le jeton
            </Button>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-2 px-4 py-3">
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-[11px] font-medium text-body">
              Nouveau jeton (ex. « PC de Lucrèce »)
            </label>
            <Input
              value={newTokenLabel}
              onChange={(event) => setNewTokenLabel(event.target.value)}
              placeholder="Libellé"
            />
          </div>
          <Button size="sm" onClick={handleCreateToken} disabled={creatingToken || !newTokenLabel.trim()}>
            {creatingToken ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />}
            Créer
          </Button>
        </div>

        <div className="divide-y divide-border border-t border-border">
          {tokens.length === 0 && (
            <p className="px-4 py-3 text-[12px] text-muted-foreground">Aucun jeton créé.</p>
          )}
          {tokens.map((token) => (
            <div key={token.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{token.label}</p>
                <p className="text-[11px] text-muted-foreground">
                  Créé le {formatDateTime(token.createdAt)}
                  {token.lastUsedAt
                    ? ` — dernier usage ${relativeTimeFromNow(token.lastUsedAt)}`
                    : " — jamais utilisé"}
                  {token.revokedAt ? " — révoqué" : ""}
                </p>
              </div>
              {!token.revokedAt && (
                <Button size="sm" variant="secondary" onClick={() => handleRevokeToken(token.id)}>
                  Révoquer
                </Button>
              )}
            </div>
          ))}
        </div>
      </section>
      )}
    </div>
  );
}
